import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import css from "./DishCreateModal.module.css";
import { DishLeftBlockCreateModal } from "../DishLeftBlockCreateModal/DishLeftBlockCreateModal";
import { DishRightBlockCreateModal } from "../DishRightBlockCreateModal/DishRightBlockCreateModal";
import {
  arrayFormattedDishProducts,
  arrayFormattedDishSemiFinished,
  dishesActions,
  dishProductsAction,
  dishSemiFinishedActions,
} from "../../../../store/slices/dishes-slices";
import { dishesService } from "../../../../services/dishes-services";
import { DishType } from "../../../../constants/enums";
import { DishAddProductModal } from "../DishAddProductModal/DishAddProductModal";
import { DishAddSemiFinishModal } from "../DishAddSemiFinishModal/DishAddSemiFinishModal";

const NUTRITION_OVERRIDE_DEFAULTS = {
  energyOverridden: false,
  proteinsOverridden: false,
  fatsOverridden: false,
  carbohydratesOverridden: false,
  costOverridden: false,
};

const DishCreateModal = ({ setIsOpen }) => {
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [uploadedImage, setUploadedImage] = useState(null);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const methods = useForm({ defaultValues: NUTRITION_OVERRIDE_DEFAULTS });
  const { setValue, watch } = methods;

  const energyOverridden = watch("energyOverridden");
  const proteinsOverridden = watch("proteinsOverridden");
  const fatsOverridden = watch("fatsOverridden");
  const carbohydratesOverridden = watch("carbohydratesOverridden");
  const costOverridden = watch("costOverridden");

  const [dishProductAddOpen, setDishProductAddOpen] = useState(false);
  const [dishSemiFinishedAddOpen, setDishSemiFinishedAddOpen] = useState(false);

  // Clear arrays when modal opens to ensure fresh state
  useEffect(() => {
    dispatch(dishProductsAction.clearArray());
    dispatch(dishSemiFinishedActions.clearArray());
  }, [dispatch]);

  const {
    dishProductsWeight,
    dishProductsWeightNetto,
    dishProductsCost,
    dishProductEnergy,
    dishProductsFats,
    dishProductsProteins,
    dishProductsCarbohydrates,
  } = useSelector((store) => store.dishProducts);

  const {
    dishSemiFinishedWeight,
    dishSemiFinishedWeightNetto,
    dishSemiFinishedCost,
    dishSemiFinishedEnergy,
    dishSemiFinishedFats,
    dishSemiFinishedProteins,
    dishSemiFinishedCarbohydrates,
  } = useSelector((store) => store.dishSemiFinished);

  useEffect(() => {
    const currentWeight = watch("weight");
    if (!currentWeight) {
      setValue("weight", dishProductsWeight + dishSemiFinishedWeight);
    }
    setValue(
      "weightNet",
      dishProductsWeightNetto + dishSemiFinishedWeightNetto,
    );
    if (!costOverridden) {
      setValue("Cost", dishProductsCost + dishSemiFinishedCost);
    }
    if (!fatsOverridden) {
      setValue("Fats", dishProductsFats + dishSemiFinishedFats);
    }
    if (!proteinsOverridden) {
      setValue("Proteins", dishProductsProteins + dishSemiFinishedProteins);
    }
    if (!carbohydratesOverridden) {
      setValue(
        "Carbohydrates",
        dishProductsCarbohydrates + dishSemiFinishedCarbohydrates,
      );
    }
    if (!energyOverridden) {
      setValue("Energy", dishProductEnergy + dishSemiFinishedEnergy);
    }
  }, [
    dishProductsWeight,
    dishSemiFinishedWeight,
    dishProductsWeightNetto,
    dishSemiFinishedWeightNetto,
    dishProductsCost,
    dishSemiFinishedCost,
    dishProductsFats,
    dishSemiFinishedFats,
    dishProductsProteins,
    dishSemiFinishedProteins,
    dishProductsCarbohydrates,
    dishSemiFinishedCarbohydrates,
    dishProductEnergy,
    dishSemiFinishedEnergy,
    setValue,
    watch,
    energyOverridden,
    proteinsOverridden,
    fatsOverridden,
    carbohydratesOverridden,
    costOverridden,
  ]);

  const formattedProducts = useSelector(arrayFormattedDishProducts);
  const formattedSemiFinished = useSelector(arrayFormattedDishSemiFinished);

  const handleCategoryChange = (value) => {
    // value is already an array from Ant Design Select
    setSelectedCategory(value);
    setValue("categories", value);
  };

  const onSubmit = async (data) => {
    try {
      const totalWeight = dishProductsWeight + dishSemiFinishedWeight;

      const formData = new FormData();
      if (uploadedImage) {
        formData.append("file", uploadedImage);
      }

      formData.append(
        "name",
        JSON.stringify({
          en: data.nameEn,
          ru: data.nameRu,
          ge: data.nameKa,
          ar: data.nameAr,
        }),
      );

      formData.append(
        "description",
        JSON.stringify({
          en: data.descriptionEn,
          ru: data.descriptionRu,
          ge: data.descriptionKa,
          ar: data.descriptionAr,
        }),
      );

      formData.append(
        "recipe",
        JSON.stringify({
          en: data.recipeEn,
          ru: data.recipeRu,
          ge: data.recipeGe,
          ar: data.recipeAr,
        }),
      );

      formData.append("img", "placeholder");
      formData.append("cost", Number(data.Cost) || 0);
      formData.append("price", parseFloat(data.price) || 0);
      formData.append(
        "weight",
        data.weight ? Number(data.weight) : totalWeight,
      );
      formData.append("weightNetto", Number(data.weightNet) || 0);
      formData.append("categories", JSON.stringify(selectedCategory || []));
      formData.append("products", JSON.stringify(formattedProducts));
      formData.append("semiFinisheds", JSON.stringify(formattedSemiFinished));
      formData.append("type", data.type || DishType.REGULAR);

      if (data.energyOverridden) {
        formData.append("energy", data.Energy);
      }
      if (data.proteinsOverridden) {
        formData.append("proteins", data.Proteins);
      }
      if (data.fatsOverridden) {
        formData.append("fats", data.Fats);
      }
      if (data.carbohydratesOverridden) {
        formData.append("carbohydrates", data.Carbohydrates);
      }

      formData.append(
        "energyOverridden",
        data.energyOverridden ? "true" : "false",
      );
      formData.append(
        "proteinsOverridden",
        data.proteinsOverridden ? "true" : "false",
      );
      formData.append("fatsOverridden", data.fatsOverridden ? "true" : "false");
      formData.append(
        "carbohydratesOverridden",
        data.carbohydratesOverridden ? "true" : "false",
      );
      formData.append("costOverridden", data.costOverridden ? "true" : "false");

      await dishesService.createDishes(formData);
      dispatch(dishesActions.changeTrigger());
      toast.success(t("New dish was created!"));
      setIsOpen(false);
    } catch (error) {
      console.error(error);
      toast.error(t("modal-toast-creating-error"));
    }
  };

  return (
    <section className={css.overlay}>
      <div className={css.modal}>
        <div className={css.header}>
          <div className={css.title}>{t("modal-new-dish")}</div>
          <div className={css.closeButton} onClick={() => setIsOpen(false)}>
            ×
          </div>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className={css.form}>
            <div className={css.infoBlock}>
              <div className={css.leftBlock}>
                <DishLeftBlockCreateModal
                  setDishProductAddOpen={setDishProductAddOpen}
                  setDishSemiFinishedAddOpen={setDishSemiFinishedAddOpen}
                  setUploadedImage={setUploadedImage}
                />
              </div>

              <div className={css.rightBlock}>
                <DishRightBlockCreateModal
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  handleCategoryChange={handleCategoryChange}
                />
              </div>
            </div>

            <div className={css.buttonsBlock}>
              <button
                type="button"
                className={css.cancelButton}
                onClick={() => setIsOpen(false)}
              >
                {t("modal-cancel")}
              </button>
              <button type="submit" className={css.submitButton}>
                OK
              </button>
            </div>
          </form>
        </FormProvider>
      </div>

      {dishProductAddOpen && (
        <DishAddProductModal setDishProductAddOpen={setDishProductAddOpen} />
      )}

      {dishSemiFinishedAddOpen && (
        <DishAddSemiFinishModal
          setDishSemiFinishedAddOpen={setDishSemiFinishedAddOpen}
        />
      )}
    </section>
  );
};

export { DishCreateModal };
